<template>
  <div class="chart-wrapper mt-2">
    <div class="chart-container">
      <highcharts
      v-if="mixedChartOptions"
      class="eventsChart"
      ref="mixedGraph"
      :options="mixedChartOptions"
      :style="{ height: dynamicHeight, width: '100%', maxWidth: '100%' }"
    />
    
    </div>
  </div>
</template>


<script>
import { Highcharts } from "highcharts-vue";
import { fetchGraphData } from "@/service/emailDetails";
import moment from "moment";

export default {
  components: { highcharts: Highcharts },
  props: {
    domainName: { type: String, required: true },
    fromDate: { type: Date, required: true },
    toDate: { type: Date, required: true },
    daysType: { type: String, required: true },
  },
  data() {
    return {
      mixedChartOptions: null,
      chartData: [],
      loading: false,
      dynamicHeight: "50vh", // Default height
    };
  },
  methods: {
    updateChartHeight() {
  const screenHeight = window.innerHeight;

  if (screenHeight > 1200) {
    this.dynamicHeight = "400px"; // was 450px
  } else if (screenHeight > 992) {
    this.dynamicHeight = "400px"; // was 400px
  } else if (screenHeight > 768) {
    this.dynamicHeight = "400px"; // was 350px
  } else {
    this.dynamicHeight = "280px"; // was 280px
  }

  this.$nextTick(() => {
    if (this.$refs.mixedGraph?.chart) {
      this.$refs.mixedGraph.chart;
    }
  });
},

    async fetchGraphData() {
  try {
    this.loading = true;
    const periodMap = {
      "7_days": "last-7",
      "15_days": "last-15", // Added 15 days period
      "30_days": "last-30",
      "60_days": "last-60",
      "90_days": "last-90",
      "custom": "custom",
    };

    const period = periodMap[this.daysType] || "custom";
    const startDate = moment(this.fromDate).isValid() ? moment(this.fromDate).format("YYYY-MM-DD") : null;
    const endDate = moment(this.toDate).isValid() ? moment(this.toDate).format("YYYY-MM-DD") : null;

    const params = { period, domain: this.domainName, startDate, endDate };
    if (period !== "custom") {
      delete params.startDate;
      delete params.endDate;
    }

    const response = await fetchGraphData(params);
    if (!Array.isArray(response?.data) || response.data.length === 0) {
      this.chartData = [];
      this.setChartOptions();
      this.loading = false;
      return;
    }

    this.chartData = response.data
      .sort((a, b) => new Date(a.date_str) - new Date(b.date_str))
      .map(el => ({
        date_str: el.date_str ? moment(el.date_str).format("DD-MMM-YYYY") : "N/A",
        inbox_percent: el.inbox_percent ?? null,
        spam_percent: el.spam_percent ?? null,
        total_emails: el.total_emails ?? null,
      }));

    this.setChartOptions();
    this.loading = false;
  } catch (error) {
    console.error("Error fetching chart data:", error);
    this.loading = false;
  }
},
    setChartOptions() {
      this.mixedChartOptions = {
        chart: { 
          zoomType: "xy", 
          backgroundColor: "#f9f9f9", 
          type: "column", 
          height: this.dynamicHeight 
        },
        title: { 
          text: `Domain: ${this.domainName}`, 
          style: { fontSize: "14px", fontWeight: "bold" } 
        },
        credits: { enabled: false },
        xAxis: {
          categories: this.chartData.map(el => el.date_str),
          labels: { rotation: -45, style: { fontSize: "10px" } },
        },
        yAxis: [
          {
            title: { text: "Email Placement (%)" },
            labels: { formatter: function () { return this.value + "%"; }, step: 1 },
            tickInterval: 25,
            max: 100,
            min: 0,
          },
          {
            title: { text: "Gmail" },
            labels: { formatter: function () { return this.value + "%"; }, step: 1 },
            opposite: true,
            tickInterval: 25,
            max: 100,
            min: 0,
          },
        ],
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            const date = this.points[0]?.category || "N/A";
            let inboxPoint = this.points.find(p => p.series.name === "Inbox");
            let spamPoint = this.points.find(p => p.series.name === "Spam");
            let inboxPercent = inboxPoint ? inboxPoint.y : 0;
            let spamPercent = spamPoint ? spamPoint.y : 0;
            let totalEmailsCount = (inboxPercent + spamPercent).toFixed(2);

            return `${date}<br/>` +
              `<span style="color:#1b5080">●</span> Inbox: ${inboxPercent.toFixed(2)}%<br/>` +
              `<span style="color:#cc0000">●</span> Spam: ${spamPercent.toFixed(2)}%<br/>` +
              `<span style="color:#38761d;">●</span> Gmail: ${totalEmailsCount}%`;
          },
        },
        plotOptions: { column: { stacking: "normal" } },
        series: [
          { name: "Spam", type: "column", data: this.chartData.map(el => el.spam_percent), color: "#cc0000", stack: "emails" },
          { name: "Inbox", type: "column", data: this.chartData.map(el => el.inbox_percent), color: "#1b5080", stack: "emails" },
          { name: "Gmail", type: "line", yAxis: 1, data: this.chartData.map(el => el.inbox_percent), marker: { enabled: true }, color: "#38761d", dashStyle: "ShortDash", lineWidth: 3 },
        ],
      };

      this.$nextTick(() => {
        if (this.$refs.mixedGraph?.chart) {
          this.$refs.mixedGraph.chart.reflow();
        }
      });
    },
  },
  watch: {
    domainName: "fetchGraphData",
    fromDate: "fetchGraphData",
    toDate: "fetchGraphData",
    daysType: "fetchGraphData",
  },
  created() {
    this.fetchGraphData();
  },
  mounted() {
    this.updateChartHeight();
    window.addEventListener("resize", this.updateChartHeight);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateChartHeight);
  },
};
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 1700px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  overflow-x: auto;
}

/* Adjust for sidebar */
@media (min-width: 1440px) {
  .chart-wrapper {
    margin-left: 100px;
    width: calc(100% - 110px); /* More width */
  }
}

@media (min-width: 1025px) and (max-width: 1439px) {
  .chart-wrapper {
    margin-left: 90px;
    width: calc(100% - 100px);
  }
}

@media (max-width: 1024px) {
  .chart-wrapper {
    margin-left: 25px; /* Shift slightly right */
    width: calc(100% - 40px); /* Adjust width accordingly */
  }
}

@media (max-width: 768px) {
  .chart-wrapper {
    margin-left: 10px; /* Shift a little more right */
    margin-right: 10px; /* Maintain balance */
    width: calc(100% - 25px);
  }
}
@media (max-width: 480px) {
  .chart-wrapper {
    margin-left: 10px; /* Slight right shift */
    margin-right: 5px;
    width: calc(100% - 20px);
  }
}


</style>
